import { route } from '@/ziggy-shim';
import AxiosClient from './AxiosClient';
import { api, apiConfig, CastValue } from './LaravelClient';

export interface UserUpdateRequest {
  name: string;
  email: string;
  projects?: { project_id: number; role_id: number }[];
  companies?: { company_id: number; role_id: number }[];
  admin: boolean;
}

export interface UserCreateRequest {
  name: string;
  email: string;
}

export default {
  async create({
    name,
    email,
  }: UserCreateRequest) {
    return await AxiosClient.postRequest(route('api.v1.users.store'), {
      name,
      email,
    });
  },

  async update(id: Id, {
    name,
    email,
    projects,
    companies,
    admin,
  }: UserUpdateRequest) {
    return await AxiosClient.patchRequest(route('api.v1.users.update', id), {
      name,
      email,
      projects,
      companies,
      admin,
    });
  },

  async updateMe(data: ApiRequests['api.v1.users.update-me']) {
    return await api(route('api.v1.users.update-me'), 'patch', {
      data,
      casts: {
        qualifications: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
      },
    });
  },

  async delete(id: Id) {
    return await AxiosClient.deleteRequest(route('api.v1.users.destroy', id));
  },

  async show(id: Id) {
    return await AxiosClient.getRequest(route('api.v1.users.show', id));
  },

  async index() {
    return await AxiosClient.getRequest(route('api.v1.users.index'));
  },

  async disable(id: Id, data: ApiRequests['api.v1.users.toggle-disable']) {
    return await api(route('api.v1.users.toggle-disable', id), 'post', {
      data,
    });
  },

  async adminIndex(page = 1) {
    return await apiConfig(route('api.v1.admin.users.index'), 'get', {
      params: {
        page,
      },
    });
  },

  signature: {
    async show() {
      return await api(route('api.v1.users.signature.show'), 'get');
    },

    async store(data: ApiRequests['api.v1.users.signature.store'] | FormData) {
      return await api(route('api.v1.users.signature.store'), 'post', {
        data,
      });
    },
  },

  tokens: {
    async index() {
      return await api(route('api.v1.users.tokens.index'), 'get');
    },

    async store(data: ApiRequests['api.v1.users.tokens.store']) {
      return await api(route('api.v1.users.tokens.store'), 'post', {
        data,
      });
    },

    async destroy(tokenId: Id) {
      return await api(route('api.v1.users.tokens.destroy', tokenId), 'delete');
    },
  },

  admin: {
    async welcomeEmail(userId: Id) {
      return await apiConfig(route('api.v1.admin.users.welcome-email', userId), 'post');
    },

    tokens: {
      async index(userId: Id) {
        return await api(route('api.v1.admin.users.tokens.index', userId), 'get');
      },

      async destroy(userId: Id, tokenId: Id) {
        return await api(route('api.v1.admin.users.tokens.destroy', [userId, tokenId]), 'delete');
      },
    },
  },

  company: {
    async index(companyId: Id) {
      return await api(route('api.v1.companies.users.index', companyId), 'get');
    },

    async attachRole(companyId: Id, projectId: Id, data: ApiRequests['api.v1.companies.user-roles.attach']) {
      return await api(route('api.v1.companies.user-roles.attach', [companyId, projectId]), 'post', {
        data,
      });
    },

    async detachRole(companyId: Id, projectId: Id, data: ApiRequests['api.v1.companies.user-roles.detach']) {
      return await api(route('api.v1.companies.user-roles.detach', [companyId, projectId]), 'delete', {
        data,
      });
    },

    async invite(companyId: Id, data: ApiRequests['api.v1.companies.users.store']) {
      return await api(route('api.v1.companies.users.store', companyId), 'post', {
        data,
        casts: {
          name: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
        },
      });
    },

    async search(companyId: Id, data: ApiRequests['api.v1.companies.users.search']) {
      return await api(route('api.v1.companies.users.search', companyId), 'get', {
        data,
      });
    },
  },

  clients: {
    async index(id: Id) {
      return await api(route('api.v1.projects.clients.index', id), 'get');
    },

    async create(id: Id, data: ApiRequests['api.v1.projects.clients.store']) {
      return await api(route('api.v1.projects.clients.store', id), 'post', {
        data,
        casts: {
          name: (value, clone, key) => new CastValue(value).cast('nullable', clone, key).get(),
        },
      });
    },

    async delete(id: Id, userId: Id) {
      return await api(route('api.v1.projects.clients.destroy', [id, userId]), 'delete');
    },

    async search(projectId: Id, data: ApiRequests['api.v1.projects.clients.search']) {
      return await api(route('api.v1.projects.clients.search', projectId), 'get', {
        data,
      });
    },
  },
};
